import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PersonFill,
  PersonVcardFill,
  PiggyBankFill,
  Bank2,
  MortarboardFill,
  BriefcaseFill,
  PeopleFill,
  CameraFill,
  FileEarmarkArrowUp,
  PlusLg,
  Trash,
} from 'react-bootstrap-icons';
import { FiHome } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import './EmployeeRegister.css';
import { apiRequest } from '../utils/api';
import { resolveFileUrl, resolveFileFromCandidates } from '../utils/fileUrl';
import { getCroppedImage } from '../utils/image';
import {
  employeeApiRequest,
  clearEmployeeRegistrationDraft,
  getEmployeeRegistrationDraft,
  getEmployeeToken,
  setEmployeeRegistrationDraft,
  getStoredEmployeeProfile,
  updateStoredEmployeeProfile,
} from '../utils/employeeApi';
import useDocumentTypes from '../hooks/useDocumentTypes';
import { BANK_DETAILS_REQUIRED, EMPLOYEE_UPLOAD_MAX_BYTES } from '../config/hrmsConfig';


const PHOTO_KEYWORDS = ['photo'];
const EMPLOYEE_PHOTO_TYPE_NAMES = new Set(['employee photo', 'profile photo', 'photo']);

const normalizeDocumentTypeName = (value) => String(value ?? '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();
const FILE_SIZE_ERROR = 'File size exceeds 10MB limit.';

const resolveUploadImageUrl = resolveFileUrl;

const appendVersionParam = (url, version) => {
  if (!url) return '';
  if (!version) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(String(version))}`;
};

const findEmployeePhotoDocument = (documents = []) => {
  if (!Array.isArray(documents)) {
    return null;
  }

  return (
    documents.find((document) => {
      const type = String(document?.documentType ?? '').toLowerCase();
      const fileName = String(document?.fileName ?? '').toLowerCase();
      return PHOTO_KEYWORDS.some((keyword) => type.includes(keyword) || fileName.includes(keyword));
    }) ?? null
  );
};

const getInitials = (value) => {
  if (!value) {
    return 'HR';
  }

  const segments = value.trim().split(/\s+/);
  const [first, second] = [segments[0], segments[segments.length - 1]];
  return `${first?.[0] ?? ''}${second?.[0] ?? ''}`.toUpperCase() || 'HR';
};

const formatFileName = (fileName, maxLength = 28) => {
  if (!fileName) {
    return '';
  }

  if (fileName.length <= maxLength) {
    return fileName.toUpperCase();
  }

  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = lastDotIndex > 0 ? fileName.slice(lastDotIndex + 1) : '';
  const baseName = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;

  const visibleLength = Math.max(4, maxLength - extension.length - 4);
  const visibleTail = baseName.slice(-visibleLength);

  const truncated = `...${visibleTail}${extension ? `.${extension}` : ''}`;
  return truncated.toUpperCase();
};

const buildAbsoluteUrl = (path) => resolveFileFromCandidates(path) || '';

const normalizeRegistrationStatus = (value) => {
  const normalized = (value || '').toString().trim().toLowerCase();
  return ['not_started', 'in_progress', 'submitted', 'approved'].includes(normalized)
    ? normalized
    : null;
};

const resolveRegistrationStatus = (profile, isProfileApproved) => {
  const candidates = [
    profile?.meta?.mobileRegistrationStatus,
    profile?.mobileRegistrationStatus,
    profile?.registrationStatus,
    profile?.meta?.registrationStatus,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeRegistrationStatus(candidate);
    if (normalized) {
      return normalized;
    }
  }

  if (isProfileApproved) {
    return 'approved';
  }

  return 'not_started';
};

const initialFormState = {
  fullName: '',
  dob: '',
  gender: '',
  maritalStatus: '',
  spouseName: '',
  fatherName: '',
  phone: '',
  aadhaar: '',
  email: '',
  currentAddress: '',
  permanentAddress: '',
  uan: '',
  esi: '',
  accountNumber: '',
  confirmAccountNumber: '',
  ifsc: '',
  bankName: '',
  panNumber: '',
  drivingLicense: '',
  lastCompany: '',
  designation: '',
  fromDate: '',
  toDate: '',
};

const emptyQualificationDraft = {
  qualificationType: '',
  courseName: '',
  institutionName: '',
  yearOfPassing: '',
  gradeOrPercentage: '',
  file: null,
  fileName: '',
};

const genderOptions = [
  { label: 'MALE', value: 'male' },
  { label: 'FEMALE', value: 'female' },
  { label: 'TRANSGENDER', value: 'other' },
];

const maritalOptions = [
  { label: 'UNMARRIED', value: 'single' },
  { label: 'MARRIED', value: 'married' },
  { label: 'DIVORCED', value: 'divorced' },
  { label: 'WIDOWED', value: 'widowed' },
];

const relationOptions = ['FATHER', 'MOTHER', 'SPOUSE', 'CHILD', 'SIBLING', 'GUARDIAN'];

const EmployeeRegister = () => {
  const navigate = useNavigate();
  const [employeeProfile, setEmployeeProfile] = useState(() => getStoredEmployeeProfile());
  const [formData, setFormData] = useState(initialFormState);
  const [sameAsCurrent, setSameAsCurrent] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [banks, setBanks] = useState([]);
  const [isLoadingBanks, setIsLoadingBanks] = useState(false);
  const [bankError, setBankError] = useState('');


  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [qualificationOptionsError, setQualificationOptionsError] = useState('');
  const [qualificationDraft, setQualificationDraft] = useState(emptyQualificationDraft);
  const [qualificationEntries, setQualificationEntries] = useState([]);
  const [qualificationError, setQualificationError] = useState('');
  const [isQualificationUploading, setIsQualificationUploading] = useState(false);

  const [documents, setDocuments] = useState([]);
  const [documentDraft, setDocumentDraft] = useState({ documentTypeId: '', documentType: '', documentTypeName: '', file: null, fileName: '' });
  const [documentError, setDocumentError] = useState('');
  const [isDocumentUploading, setIsDocumentUploading] = useState(false);

  const [familyMemberForm, setFamilyMemberForm] = useState({ relation: '', name: '', mobile: '', dob: '' });
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyError, setFamilyError] = useState('');
  const [hasExperience, setHasExperience] = useState(false);

  const [photoDocument, setPhotoDocument] = useState(null);
  const [photoFileName, setPhotoFileName] = useState('');
  const [photoSource, setPhotoSourceState] = useState('');
  const [photoCrop, setPhotoCrop] = useState({ x: 0, y: 0 });
  const [photoZoom, setPhotoZoom] = useState(1);
  const [photoCroppedAreaPixels, setPhotoCroppedAreaPixels] = useState(null);
  const [isCroppingPhoto, setIsCroppingPhoto] = useState(false);
  const [photoPreview, setPhotoPreviewState] = useState('');
  const [photoUploadError, setPhotoUploadError] = useState('');
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const [headerPhotoLoadFailed, setHeaderPhotoLoadFailed] = useState(false);

  const qualificationFileInputRef = useRef(null);
  const documentFileInputRef = useRef(null);
  const photoFileInputRef = useRef(null);
  const photoSourceRef = useRef('');
  const photoPreviewRef = useRef({ url: '', isObjectUrl: false });
  const prefillAppliedRef = useRef(false);


  const {
    documentTypes,
    mandatoryDocumentTypes,
    isLoading: isLoadingDocumentTypes,
    error: documentTypeError,
  } = useDocumentTypes({ activeOnly: true });

  const availableDocumentTypes = useMemo(
    () => documentTypes.filter((type) => type.isActive),
    [documentTypes],
  );

  const employeePhotoDocumentType = useMemo(
    () => availableDocumentTypes.find((type) =>
      EMPLOYEE_PHOTO_TYPE_NAMES.has(normalizeDocumentTypeName(type?.name)),
    ) ?? null,
    [availableDocumentTypes],
  );

  const employeeDocuments = useMemo(
    () => (Array.isArray(employeeProfile?.documents) ? employeeProfile.documents : []),
    [employeeProfile?.documents],
  );

  const employeePhotoUrl = useMemo(() => {
    const version =
      employeeProfile?.updatedAt || employeeProfile?.photoUpdatedAt || Date.now();
    const candidateValues = [
      employeeProfile?.photoUrl,
      employeeProfile?.photoFilePath,
      employeeProfile?.photo,
      employeeProfile?.photoName,
    ];

    for (const candidate of candidateValues) {
      const resolved = resolveUploadImageUrl(candidate);
      if (resolved) {
        return appendVersionParam(resolved, version);
      }
    }

    const photoDocument = findEmployeePhotoDocument(employeeDocuments);
    if (photoDocument) {
      const resolvedDocumentPath =
        resolveUploadImageUrl(photoDocument.filePath) || resolveUploadImageUrl(photoDocument.fileUrl);
      if (resolvedDocumentPath) {
        return appendVersionParam(resolvedDocumentPath, version);
      }
    }

    return null;
  }, [
    employeeDocuments,
    employeeProfile?.photo,
    employeeProfile?.photoFilePath,
    employeeProfile?.photoName,
    employeeProfile?.photoUpdatedAt,
    employeeProfile?.photoUrl,
    employeeProfile?.updatedAt,
  ]);

  useEffect(() => {
    setHeaderPhotoLoadFailed(false);
  }, [employeePhotoUrl]);

  const employeeName = (employeeProfile?.name || '').trim() || 'Employee';
  const companyName =
    employeeProfile?.company?.name ||
    employeeProfile?.companyName ||
    employeeProfile?.outlet?.companyName ||
    '';
  const shouldShowHeaderPhoto = Boolean(employeePhotoUrl && !headerPhotoLoadFailed);
  const employeeAvatarAlt = employeeName ? `${employeeName} photo` : 'Employee photo';

  const approvalStatus = (employeeProfile?.approvalStatus || employeeProfile?.status || '').toString().toLowerCase();
  const isProfileApproved = ['approved', 'active', 'verified'].includes(approvalStatus);

  const registrationStatus = useMemo(
    () => resolveRegistrationStatus(employeeProfile, isProfileApproved),
    [employeeProfile, isProfileApproved],
  );
  const isRegistrationBlocked = ['submitted', 'approved'].includes(registrationStatus);
  const hasMarkedRegistrationProgress = useRef(false);

  const educationMandatory = useMemo(() => {
    const candidates = [
      employeeProfile?.meta?.educationMandatory,
      employeeProfile?.educationMandatory,
    ];

    for (const candidate of candidates) {
      if (candidate !== undefined && candidate !== null) {
        return Boolean(candidate);
      }
    }

    return true;
  }, [employeeProfile?.educationMandatory, employeeProfile?.meta?.educationMandatory]);

  useEffect(() => {
    if (!educationMandatory) {
      setQualificationError('');
      setFormErrors((previous) => {
        const { qualificationsField, ...rest } = previous;
        return rest;
      });
    }
  }, [educationMandatory]);

  const sectionHeadings = useMemo(
    () => [
      { title: 'PERSONAL INFORMATION', icon: <PersonVcardFill /> },
      { title: 'PF INFORMATION', icon: <PiggyBankFill /> },
      { title: 'BANK INFORMATION', icon: <Bank2 /> },
      { title: 'EDUCATION QUALIFICATIONS', icon: <MortarboardFill /> },
      { title: 'PROFESSIONAL INFORMATION', icon: <BriefcaseFill /> },
      { title: 'FAMILY INFORMATION', icon: <PeopleFill /> },
      { title: 'EMPLOYEE PHOTO', icon: <CameraFill /> },
      { title: 'DOCUMENTS', icon: <FileEarmarkArrowUp /> },
    ],
    [],
  );

  const shouldCollectSpouseName = formData.maritalStatus === 'married' && formData.gender === 'female';

  // ✅ Auth check
  useEffect(() => {
    const token = getEmployeeToken();
    if (!token) {
      navigate('/employee/mobile/login', { replace: true });
    }
  }, [navigate]);

  // ✅ Load employee profile
  useEffect(() => {
    const token = getEmployeeToken();
    if (!token) {
      return;
    }

    let isMounted = true;

    const loadEmployeeProfile = async () => {
      try {
        const response = await employeeApiRequest('/api/public/employee/me');
        if (!isMounted) {
          return;
        }
        setEmployeeProfile(response.data ?? null);
        updateStoredEmployeeProfile(response.data ?? null);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load employee profile for registration.', error);
      }
    };

    loadEmployeeProfile();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (hasMarkedRegistrationProgress.current || !employeeProfile || isRegistrationBlocked) {
      return undefined;
    }

    let isMounted = true;
    hasMarkedRegistrationProgress.current = true;

    const markRegistrationInProgress = async () => {
      try {
        const response = await employeeApiRequest('/api/public/employee/registration/status', {
          method: 'PATCH',
          body: { status: 'in_progress' },
        });

        if (!isMounted) {
          return;
        }

        const status = response?.data?.status ?? 'in_progress';
        const startedAt = response?.data?.startedAt ?? employeeProfile?.meta?.mobileRegistrationStartedAt ?? null;
        const submittedAt = response?.data?.submittedAt ?? employeeProfile?.meta?.mobileRegistrationSubmittedAt ?? null;
        const approvedAt = response?.data?.approvedAt ?? employeeProfile?.meta?.mobileRegistrationApprovedAt ?? null;

        const updatedProfile = {
          ...(employeeProfile ?? {}),
          registrationStatus: status,
          mobileRegistrationStatus: status,
          meta: {
            ...(employeeProfile?.meta ?? {}),
            mobileRegistrationStatus: status,
            mobileRegistrationStartedAt: startedAt,
            mobileRegistrationSubmittedAt: submittedAt,
            mobileRegistrationApprovedAt: approvedAt,
          },
        };

        setEmployeeProfile(updatedProfile);
        updateStoredEmployeeProfile(updatedProfile);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update registration status', error);
      }
    };

    markRegistrationInProgress();

    return () => {
      isMounted = false;
    };
  }, [employeeProfile, isRegistrationBlocked]);

  // ✅ Photo helpers (defined early so they can be used everywhere)
  const updatePhotoSource = useCallback((url) => {
    if (photoSourceRef.current) {
      URL.revokeObjectURL(photoSourceRef.current);
    }
    photoSourceRef.current = url || '';
    setPhotoSourceState(url || '');
  }, []);

  const updatePhotoPreview = useCallback((url, isObjectUrl = false) => {
    if (photoPreviewRef.current.isObjectUrl && photoPreviewRef.current.url) {
      URL.revokeObjectURL(photoPreviewRef.current.url);
    }
    photoPreviewRef.current = { url: url || '', isObjectUrl };
    setPhotoPreviewState(url || '');
  }, []);

  // ✅ Prefill from profile
  const applyProfilePrefill = useCallback(
    (profile) => {
      if (!profile || prefillAppliedRef.current) {
        return;
      }

      const personal = profile.personal || {};
      const pf = profile.pf || {};
      const bank = profile.bank || {};
      const professional = profile.professional || {};
      const educationQualifications = Array.isArray(profile.education?.qualifications)
        ? profile.education.qualifications
        : [];
      const familyList = Array.isArray(profile.family) ? profile.family : [];
      const documentsFromProfile = Array.isArray(profile.documents) ? profile.documents : [];

      const toUpper = (value) => (value ? String(value).toUpperCase() : '');
      const normalizeDigits = (value, maxLength) => {
        if (!value) {
          return '';
        }
        const digits = String(value).replace(/\D/g, '');
        if (typeof maxLength === 'number') {
          if (digits.length > maxLength) {
            return digits.slice(-maxLength);
          }
          return digits.slice(0, maxLength);
        }
        return digits;
      };

      const phoneValue = normalizeDigits(profile.phone || personal.phone, 10);
      const aadhaarValue = normalizeDigits(
        profile.aadhar || personal.aadhaar || personal.aadharNumber,
        12,
      );

      const normalizedFormData = {
        fullName: toUpper(personal.name || profile.name),
        dob: personal.dob || '',
        gender: personal.gender ? String(personal.gender).toLowerCase() : '',
        maritalStatus: personal.maritalStatus
          ? String(personal.maritalStatus).toLowerCase()
          : '',
        spouseName: toUpper(personal.spouseName),
        fatherName: toUpper(personal.fatherName),
        phone: phoneValue,
        aadhaar: aadhaarValue,
        email: personal.email || profile.email || '',
        currentAddress: toUpper(personal.currentAddress),
        permanentAddress: toUpper(personal.permanentAddress),
        uan: pf.uanNumber || '',
        esi: pf.esiNumber || '',
        accountNumber: bank.accountNumber || '',
        confirmAccountNumber: bank.accountNumber || '',
        ifsc: toUpper(bank.ifscCode),
        bankName: toUpper(bank.bankName),
        panNumber: toUpper(bank.panCard),
        drivingLicense: toUpper(bank.drivingLicense),
        lastCompany: toUpper(professional.lastCompany),
        designation: toUpper(professional.designation),
        fromDate: professional.fromDate || '',
        toDate: professional.toDate || '',
      };

      setFormData((prev) => ({ ...prev, ...normalizedFormData }));

      const hasProfessionalDetails = Boolean(
        normalizedFormData.lastCompany ||
        normalizedFormData.designation ||
        normalizedFormData.fromDate ||
        normalizedFormData.toDate
      );
      setHasExperience(hasProfessionalDetails);

      const addressesMatch =
        normalizedFormData.currentAddress &&
        normalizedFormData.permanentAddress &&
        normalizedFormData.currentAddress === normalizedFormData.permanentAddress;
      setSameAsCurrent(Boolean(personal.sameAsCurrentAddress) || addressesMatch);

      const normalizedFamilyMembers = familyList
        .filter((member) => member)
        .map((member, index) => ({
          id: member.id ?? `prefill-family-${index}`,
          relation: toUpper(member.relation),
          name: toUpper(member.name),
          mobile: normalizeDigits(member.mobile, 10),
          dob: member.dob || '',
        }));
      setFamilyMembers(normalizedFamilyMembers);

      const normalizedQualifications = educationQualifications.map((entry, index) => ({
        id: entry.id ?? `prefill-qualification-${index}`,
        qualificationType: toUpper(entry.qualificationType || entry.title),
        courseName: toUpper(entry.courseName),
        institutionName: toUpper(entry.institutionName),
        yearOfPassing: entry.yearOfPassing ? String(entry.yearOfPassing).slice(0, 4) : '',
        gradeOrPercentage: toUpper(entry.gradeOrPercentage || entry.grade),
        documentPath: entry.documentPath || entry.filePath || '',
        documentUrl: entry.documentUrl || entry.fileUrl || '',
        fileName: toUpper(entry.fileName),
      }));
      setQualificationEntries(normalizedQualifications);

      const photoDocumentFromProfile = findEmployeePhotoDocument(documentsFromProfile);
      let remainingDocuments = documentsFromProfile;
      let nextPhotoDocument = null;

      if (photoDocumentFromProfile) {
        nextPhotoDocument = {
          documentTypeId: photoDocumentFromProfile.documentTypeId ?? photoDocumentFromProfile.document_type_id ?? employeePhotoDocumentType?.id ?? null,
          documentType:
            photoDocumentFromProfile.documentTypeName ||
            photoDocumentFromProfile.documentType ||
            'EMPLOYEE PHOTO',
          documentTypeName:
            photoDocumentFromProfile.documentTypeName ||
            photoDocumentFromProfile.documentType ||
            'EMPLOYEE PHOTO',
          fileName: toUpper(photoDocumentFromProfile.fileName) || 'EMPLOYEE PHOTO',
          filePath: photoDocumentFromProfile.filePath || '',
          fileUrl: photoDocumentFromProfile.fileUrl || '',
          mimeType: photoDocumentFromProfile.mimeType || '',
          size:
            typeof photoDocumentFromProfile.size === 'number'
              ? photoDocumentFromProfile.size
              : null,
        };
        remainingDocuments = documentsFromProfile.filter((doc) => doc !== photoDocumentFromProfile);
      } else if (profile.photoFilePath || profile.photoUrl) {
        nextPhotoDocument = {
          documentTypeId: employeePhotoDocumentType?.id ?? null,
          documentType: employeePhotoDocumentType?.name || 'EMPLOYEE PHOTO',
          documentTypeName: employeePhotoDocumentType?.name || 'EMPLOYEE PHOTO',
          fileName: toUpper(profile.photoName) || 'EMPLOYEE PHOTO',
          filePath: profile.photoFilePath || '',
          fileUrl: profile.photoUrl || profile.photoFilePath || '',
          mimeType: profile.photoMimeType || '',
          size: typeof profile.photoSize === 'number' ? profile.photoSize : null,
        };
      }

      if (nextPhotoDocument) {
        setPhotoDocument(nextPhotoDocument);
        const previewUrl =
          resolveUploadImageUrl(nextPhotoDocument.fileUrl || nextPhotoDocument.filePath) ||
          resolveUploadImageUrl(profile.photoUrl || profile.photoFilePath);
        if (previewUrl) {
          updatePhotoPreview(previewUrl, false);
        }
      }

      const normalizedDocuments = remainingDocuments
        .filter((doc) => doc)
        .map((doc, index) => ({
          id: doc.id ?? `prefill-document-${index}`,
          documentTypeId: doc.documentTypeId ?? doc.document_type_id ?? null,
          documentType: toUpper(doc.documentTypeName || doc.documentType),
          documentTypeName: doc.documentTypeName || doc.documentType || '',
          fileName: toUpper(doc.fileName),
          filePath: doc.filePath || '',
          fileUrl: doc.fileUrl || '',
          mimeType: doc.mimeType || '',
          size: typeof doc.size === 'number' ? doc.size : null,
        }));
      setDocuments(normalizedDocuments);

      prefillAppliedRef.current = true;
    },
    [employeePhotoDocumentType, updatePhotoPreview],
  );

  // ✅ Run prefill when profile loads
  useEffect(() => {
    applyProfilePrefill(employeeProfile);
  }, [applyProfilePrefill, employeeProfile]);

  useEffect(() => {
    if (!employeePhotoDocumentType?.id) return;
    setPhotoDocument((current) => {
      if (!current || current.documentTypeId) return current;
      return {
        ...current,
        documentTypeId: employeePhotoDocumentType.id,
        documentType: employeePhotoDocumentType.name,
        documentTypeName: employeePhotoDocumentType.name,
      };
    });
  }, [employeePhotoDocumentType]);

  // ✅ Load banks, qualification options
  useEffect(() => {
    let isMounted = true;

    const loadBanks = async () => {
      try {
        setIsLoadingBanks(true);
        setBankError('');
        const response = await apiRequest('/api/public/banks', { skipAuthRedirect: true });
        if (!isMounted) {
          return;
        }
        const data = Array.isArray(response.data) ? response.data : [];
        setBanks(data);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setBanks([]);
        setBankError(error.message || 'FAILED TO LOAD BANKS.');
      } finally {
        if (isMounted) {
          setIsLoadingBanks(false);
        }
      }
    };


    const loadQualificationOptions = async () => {
      try {
        setQualificationOptionsError('');
        const response = await apiRequest('/api/public/education-qualifications', { skipAuthRedirect: true });
        if (!isMounted) {
          return;
        }
        const data = Array.isArray(response.data) ? response.data : [];
        setQualificationOptions(data);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setQualificationOptions([]);
        setQualificationOptionsError(error.message || 'FAILED TO LOAD EDUCATION QUALIFICATIONS.');
      }
    };

    loadBanks();
    loadQualificationOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  // ✅ Cleanup object URLs on unmount
  useEffect(() => () => {
    if (photoSourceRef.current) {
      URL.revokeObjectURL(photoSourceRef.current);
      photoSourceRef.current = '';
    }
    if (photoPreviewRef.current.isObjectUrl && photoPreviewRef.current.url) {
      URL.revokeObjectURL(photoPreviewRef.current.url);
      photoPreviewRef.current = { url: '', isObjectUrl: false };
    }
  }, []);

  const handleNavigateHome = () => {
    navigate('/employee/mobile/details');
  };

  const removeFieldError = (fieldName) => {
    if (!formErrors[fieldName]) {
      return;
    }
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const handleIfscAutofill = (rawValue) => {
    const value = rawValue ? rawValue.trim().toUpperCase() : '';
    if (value.length < 4) {
      return;
    }

    const normalized = value.replace(/\s+/g, '');
    const exactMatch = banks.find(
      (bank) => (bank.ifscCode || '').toUpperCase() === normalized,
    );
    const prefixMatch = banks.find((bank) =>
      (bank.ifscCode || '').toUpperCase().startsWith(normalized),
    );
    const matched = exactMatch || prefixMatch;

    if (matched && matched.bankName) {
      setFormData((prev) => ({ ...prev, bankName: matched.bankName.toUpperCase() }));
      removeFieldError('bankName');
    }
  };

  const validateOptionalDigitField = useCallback((fieldKey, value, expectedLength, message) => {
    setFormErrors((prev) => {
      const next = { ...prev };
      if (value && value.length !== expectedLength) {
        next[fieldKey] = message;
      } else {
        delete next[fieldKey];
      }
      return next;
    });
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === 'phone' || name === 'aadhaar') {
      nextValue = value.replace(/\D/g, '');
      if (name === 'phone') {
        nextValue = nextValue.slice(0, 10);
      }
      if (name === 'aadhaar') {
        nextValue = nextValue.slice(0, 12);
      }
    }

    if (name === 'accountNumber' || name === 'confirmAccountNumber') {
      nextValue = value.replace(/[^0-9]/g, '');
    }

    if (name === 'uan') {
      nextValue = value.replace(/\D/g, '').slice(0, 12);
      validateOptionalDigitField('uan', nextValue, 12, 'UAN NUMBER MUST BE 12 DIGITS.');
    }

    if (name === 'esi') {
      nextValue = value.replace(/\D/g, '').slice(0, 10);
      validateOptionalDigitField('esi', nextValue, 10, 'ESI NUMBER MUST BE 10 DIGITS.');
    }

    if (['ifsc', 'panNumber', 'drivingLicense', 'bankName', 'fullName', 'fatherName', 'spouseName', 'currentAddress', 'permanentAddress', 'lastCompany'].includes(name)) {
      nextValue = nextValue.toUpperCase();
    }

    setFormData((prev) => {
      const updated = { ...prev, [name]: nextValue };
      if (sameAsCurrent && name === 'currentAddress') {
        updated.permanentAddress = nextValue;
      }
      if (name === 'maritalStatus' && nextValue !== 'married') {
        updated.spouseName = '';
      }
      if (name === 'gender' && nextValue !== 'female') {
        updated.spouseName = '';
      }
      return updated;
    });

    if (!['uan', 'esi'].includes(name)) {
      removeFieldError(name);
    }

    if (name === 'ifsc') {
      handleIfscAutofill(nextValue);
    }
  };

  const handleExperienceToggle = (value) => {
    setHasExperience(value);

    if (!value) {
      setFormData((prev) => ({
        ...prev,
        lastCompany: '',
        designation: '',
        fromDate: '',
        toDate: '',
      }));

      setFormErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors.lastCompany;
        delete nextErrors.designation;
        delete nextErrors.fromDate;
        delete nextErrors.toDate;
        return nextErrors;
      });
    }
  };

  const handleSameAddressToggle = (event) => {
    const { checked } = event.target;
    setSameAsCurrent(checked);
    setFormData((prev) => ({
      ...prev,
      permanentAddress: checked ? prev.currentAddress : prev.permanentAddress,
    }));
    if (checked) {
      removeFieldError('permanentAddress');
    }
  };

  const handleFamilyMemberChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;
    if (name === 'mobile') {
      nextValue = value.replace(/\D/g, '').slice(0, 10);
    }
    setFamilyMemberForm((prev) => ({ ...prev, [name]: nextValue.toUpperCase() }));
    setFamilyError('');
  };

  const handleAddFamilyMember = () => {
    if (!familyMemberForm.relation || !familyMemberForm.name) {
      setFamilyError('RELATION AND NAME ARE REQUIRED.');
      return;
    }

    if (familyMemberForm.mobile && familyMemberForm.mobile.length !== 10) {
      setFamilyError('FAMILY MEMBER MOBILE MUST BE 10 DIGITS.');
      return;
    }

    setFamilyMembers((prev) => [
      ...prev,
      {
        id: Date.now(),
        relation: familyMemberForm.relation,
        name: familyMemberForm.name,
        mobile: familyMemberForm.mobile,
        dob: familyMemberForm.dob,
      },
    ]);
    setFamilyMemberForm({ relation: '', name: '', mobile: '', dob: '' });
    setFamilyError('');
  };

  const handleRemoveFamilyMember = (id) => {
    setFamilyMembers((prev) => prev.filter((member) => member.id !== id));
  };

  const handleQualificationDraftChange = (event) => {
    const { name, value, files } = event.target;
    if (name === 'file') {
      const file = files && files.length > 0 ? files[0] : null;
      if (file && file.size > EMPLOYEE_UPLOAD_MAX_BYTES) {
        setQualificationError(FILE_SIZE_ERROR);
        return;
      }
      setQualificationDraft((prev) => ({
        ...prev,
        file,
        fileName: file ? file.name.toUpperCase() : '',
      }));
      setQualificationError('');
      return;
    }

    let nextValue = value;
    if (name === 'yearOfPassing') {
      nextValue = value.replace(/\D/g, '').slice(0, 4);
    }
    setQualificationDraft((prev) => ({ ...prev, [name]: nextValue.toUpperCase() }));
    setQualificationError('');
  };

  const resetQualificationDraft = () => {
    setQualificationDraft(emptyQualificationDraft);
    if (qualificationFileInputRef.current) {
      qualificationFileInputRef.current.value = '';
    }
  };

  const handleAddQualification = async () => {
    if (isQualificationUploading) {
      return;
    }

    const { qualificationType, courseName, institutionName, yearOfPassing, gradeOrPercentage, file, fileName } = qualificationDraft;
    if (!qualificationType || !courseName || !institutionName || !yearOfPassing || !file) {
      setQualificationError('ALL QUALIFICATION FIELDS AND DOCUMENT ARE REQUIRED.');
      return;
    }

    if (yearOfPassing.length !== 4) {
      setQualificationError('YEAR OF PASSING MUST BE FOUR DIGITS.');
      return;
    }

    try {
      setIsQualificationUploading(true);
      setQualificationError('');
      const payload = new FormData();
      payload.append('file', file);
      const { data } = await apiRequest('/api/public/education-qualifications/upload', {
        method: 'POST',
        body: payload,
        skipAuthRedirect: true,
      });

      const nextEntry = {
        id: Date.now(),
        qualificationType,
        courseName,
        institutionName,
        yearOfPassing,
        gradeOrPercentage,
        documentPath: data?.filePath || '',
        documentUrl: data?.fileUrl || '',
        fileName: (data?.fileName || fileName || file.name || '').toUpperCase(),
      };

      setQualificationEntries((prev) => [...prev, nextEntry]);
      resetQualificationDraft();
    } catch (error) {
      setQualificationError(error.message || 'FAILED TO UPLOAD QUALIFICATION DOCUMENT.');
    } finally {
      setIsQualificationUploading(false);
    }
  };

  const handleRemoveQualification = (id) => {
    setQualificationEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleDocumentDraftChange = (event) => {
    const { name, value, files } = event.target;
    if (name === 'file') {
      const file = files && files.length > 0 ? files[0] : null;
      if (file && file.size > EMPLOYEE_UPLOAD_MAX_BYTES) {
        setDocumentError(FILE_SIZE_ERROR);
        return;
      }
      setDocumentDraft((prev) => ({
        ...prev,
        file,
        fileName: file ? file.name.toUpperCase() : '',
      }));
      setDocumentError('');
      return;
    }

    if (name === 'documentTypeId') {
      const selectedType = availableDocumentTypes.find((type) => String(type.id) === value);
      setDocumentDraft((prev) => ({
        ...prev,
        documentTypeId: value,
        documentType: selectedType?.name?.toUpperCase() || '',
        documentTypeName: selectedType?.name || '',
      }));
      setDocumentError('');
      return;
    }

    setDocumentDraft((prev) => ({ ...prev, [name]: value.toUpperCase() }));
    setDocumentError('');
  };

  const resetDocumentDraft = () => {
    setDocumentDraft({ documentTypeId: '', documentType: '', documentTypeName: '', file: null, fileName: '' });
    if (documentFileInputRef.current) {
      documentFileInputRef.current.value = '';
    }
  };

  const handleAddDocument = async () => {
    if (isDocumentUploading) {
      return;
    }

    const { documentTypeId, documentType, documentTypeName, file, fileName } = documentDraft;
    const selectedType = availableDocumentTypes.find((type) => String(type.id) === String(documentTypeId));

    if (!selectedType || !file) {
      setDocumentError('SELECT A DOCUMENT TYPE AND UPLOAD THE FILE.');
      return;
    }

    try {
      setIsDocumentUploading(true);
      setDocumentError('');
      const payload = new FormData();
      payload.append('file', file);
      payload.append('documentTypeId', selectedType.id);
      payload.append('documentTypeName', selectedType.name);
      const { data } = await apiRequest('/api/public/documents/upload', {
        method: 'POST',
        body: payload,
        skipAuthRedirect: true,
      });

      const nextDocument = {
        id: Date.now(),
        documentTypeId: selectedType.id,
        documentType: selectedType.name?.toUpperCase() || documentType,
        documentTypeName: selectedType.name || documentTypeName,
        fileName: (data?.fileName || fileName || file.name || '').toUpperCase(),
        filePath: data?.filePath || '',
        fileUrl: data?.fileUrl || '',
        mimeType: data?.mimeType || file.type || '',
        size: typeof data?.size === 'number' ? data.size : file.size || null,
      };

      setDocuments((prev) => [...prev, nextDocument]);
      resetDocumentDraft();
    } catch (error) {
      setDocumentError(error.message || 'FAILED TO UPLOAD DOCUMENT.');
    } finally {
      setIsDocumentUploading(false);
    }
  };

  const handleRemoveDocument = (id) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== id));
  };

  const handlePhotoFileChange = (event) => {
    if (!employeePhotoDocumentType?.id) {
      setPhotoUploadError('Employee Photo document type is not configured. Please contact Admin.');
      if (photoFileInputRef.current) photoFileInputRef.current.value = '';
      return;
    }

    const file = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null;
    if (!file) {
      return;
    }

    if (!file.type || !file.type.startsWith('image/')) {
      setPhotoUploadError('PLEASE SELECT A VALID IMAGE FILE.');
      return;
    }
    if (file.size > EMPLOYEE_UPLOAD_MAX_BYTES) {
      setPhotoUploadError(FILE_SIZE_ERROR);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    updatePhotoSource(objectUrl);
    setPhotoCrop({ x: 0, y: 0 });
    setPhotoZoom(1);
    setPhotoCroppedAreaPixels(null);
    setIsCroppingPhoto(true);
    setPhotoFileName(file.name);
    setPhotoUploadError('');
  };

  const handlePhotoCropComplete = (_area, croppedAreaPixels) => {
    setPhotoCroppedAreaPixels(croppedAreaPixels);
  };

  const derivePhotoFileName = () => {
    if (!photoFileName) {
      return 'employee-photo.jpg';
    }
    const lower = photoFileName.toLowerCase();
    const dotIndex = lower.lastIndexOf('.');
    const base = dotIndex > 0 ? lower.slice(0, dotIndex) : lower;
    const sanitized = base.replace(/[^a-z0-9_-]/g, '').slice(0, 40) || 'employee-photo';
    return `${sanitized}-cropped.jpg`;
  };

  const handleCancelPhotoCrop = () => {
    setIsCroppingPhoto(false);
    updatePhotoSource('');
    setPhotoCroppedAreaPixels(null);
    setPhotoUploadError('');
    if (photoFileInputRef.current) {
      photoFileInputRef.current.value = '';
    }
  };

  const handleConfirmPhoto = async () => {
    if (!employeePhotoDocumentType?.id) {
      setPhotoUploadError('Employee Photo document type is not configured. Please contact Admin.');
      return;
    }

    if (!photoSource || !photoCroppedAreaPixels) {
      setPhotoUploadError('SELECT AN AREA TO CROP THE PHOTO.');
      return;
    }

    try {
      setIsPhotoUploading(true);
      setPhotoUploadError('');
      const croppedBlob = await getCroppedImage(photoSource, photoCroppedAreaPixels);
      if (!croppedBlob) {
        throw new Error('FAILED TO PROCESS THE CROPPED PHOTO.');
      }

      const fileName = derivePhotoFileName();
      const croppedFile = new File([croppedBlob], fileName, { type: 'image/jpeg' });
      const payload = new FormData();
      payload.append('file', croppedFile);
      payload.append('documentTypeId', employeePhotoDocumentType.id);
      payload.append('documentTypeName', employeePhotoDocumentType.name);
      payload.append('employeeName', formData.fullName || employeeName);

      const { data } = await apiRequest('/api/public/documents/upload', {
        method: 'POST',
        body: payload,
        skipAuthRedirect: true,
      });

      const fileUrl = data?.fileUrl || '';
      const fallbackPreview = URL.createObjectURL(croppedBlob);
      const previewUrl = fileUrl || fallbackPreview;

      updatePhotoPreview(previewUrl, !fileUrl);
      setPhotoDocument({
        documentTypeId: employeePhotoDocumentType.id,
        documentType: employeePhotoDocumentType.name,
        documentTypeName: employeePhotoDocumentType.name,
        fileName: (data?.fileName || fileName || '').toUpperCase(),
        filePath: data?.filePath || '',
        fileUrl,
        mimeType: data?.mimeType || 'image/jpeg',
        size: typeof data?.size === 'number' ? data.size : croppedBlob.size || null,
      });

      setIsCroppingPhoto(false);
      updatePhotoSource('');
      setPhotoCroppedAreaPixels(null);
      if (photoFileInputRef.current) {
        photoFileInputRef.current.value = '';
      }
    } catch (error) {
      setPhotoUploadError(error.message || 'FAILED TO UPLOAD PHOTO.');
    } finally {
      setIsPhotoUploading(false);
    }
  };

  const focusAndScrollToField = (fieldName) => {
    if (!fieldName || typeof window === 'undefined') {
      return;
    }

    const performScroll = () => {
      const fieldElement = document.querySelector(`[name="${fieldName}"]`);
      if (!fieldElement) {
        return;
      }

      const container =
        fieldElement.closest('.employee-register-field') ||
        fieldElement.closest('.employee-register-section') ||
        fieldElement;

      if (container && typeof container.scrollIntoView === 'function') {
        container.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      if (typeof fieldElement.focus === 'function') {
        try {
          fieldElement.focus({ preventScroll: true });
        } catch (error) {
          fieldElement.focus();
        }
      }
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(performScroll);
    } else {
      setTimeout(performScroll, 0);
    }
  };

  const scrollToFirstError = (errors) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length === 0) {
      return;
    }
    focusAndScrollToField(errorFields[0]);
  };

  const validateForm = () => {
    const errors = {};
    const asString = (value) => (typeof value === 'string' ? value.trim() : value);

    if (!asString(formData.fullName)) {
      errors.fullName = 'FULL NAME IS REQUIRED.';
    }
    if (!asString(formData.dob)) {
      errors.dob = 'DATE OF BIRTH IS REQUIRED.';
    }
    if (!asString(formData.gender)) {
      errors.gender = 'PLEASE SELECT GENDER.';
    }
    if (!asString(formData.maritalStatus)) {
      errors.maritalStatus = 'PLEASE SELECT MARITAL STATUS.';
    }
    if (!asString(formData.fatherName)) {
      errors.fatherName = 'FATHER NAME IS REQUIRED.';
    }
    if (!asString(formData.phone)) {
      errors.phone = 'PHONE NUMBER IS REQUIRED.';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      errors.phone = 'PHONE NUMBER MUST BE 10 DIGITS.';
    }

    if (!asString(formData.aadhaar)) {
      errors.aadhaar = 'AADHAAR NUMBER IS REQUIRED.';
    } else if (!/^\d{12}$/.test(formData.aadhaar)) {
      errors.aadhaar = 'AADHAAR NUMBER MUST BE 12 DIGITS.';
    }

    if (!asString(formData.email)) {
      errors.email = 'EMAIL IS REQUIRED.';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      errors.email = 'ENTER A VALID EMAIL ADDRESS.';
    }

    if (!asString(formData.currentAddress)) {
      errors.currentAddress = 'CURRENT ADDRESS IS REQUIRED.';
    }

    if (!sameAsCurrent && !asString(formData.permanentAddress)) {
      errors.permanentAddress = 'PERMANENT ADDRESS IS REQUIRED.';
    }

    if (asString(formData.uan) && !/^\d{12}$/.test(formData.uan)) {
      errors.uan = 'UAN NUMBER MUST BE 12 DIGITS.';
    }

    if (asString(formData.esi) && !/^\d{10}$/.test(formData.esi)) {
      errors.esi = 'ESI NUMBER MUST BE 10 DIGITS.';
    }

    if (!asString(formData.accountNumber)) {
      errors.accountNumber = 'ACCOUNT NUMBER IS REQUIRED.';
    } else if (!/^\d+$/.test(formData.accountNumber)) {
      errors.accountNumber = 'ACCOUNT NUMBER MUST CONTAIN DIGITS ONLY.';
    }

    if (!asString(formData.confirmAccountNumber)) {
      errors.confirmAccountNumber = 'PLEASE CONFIRM ACCOUNT NUMBER.';
    } else if (!/^\d+$/.test(formData.confirmAccountNumber)) {
      errors.confirmAccountNumber = 'RE-ENTERED ACCOUNT NUMBER MUST CONTAIN DIGITS ONLY.';
    }

    if (
      asString(formData.accountNumber) &&
      asString(formData.confirmAccountNumber) &&
      !errors.accountNumber &&
      !errors.confirmAccountNumber &&
      formData.accountNumber.trim() !== formData.confirmAccountNumber.trim()
    ) {
      errors.confirmAccountNumber = 'ACCOUNT NUMBERS MUST MATCH.';
    }

    if (!asString(formData.ifsc)) {
      errors.ifsc = 'IFSC CODE IS REQUIRED.';
    }

    if (BANK_DETAILS_REQUIRED && !asString(formData.bankName)) {
      errors.bankName = 'BANK NAME IS REQUIRED.';
    }

    if (!asString(formData.panNumber)) {
      errors.panNumber = 'PAN NUMBER IS REQUIRED.';
    }

    if (shouldCollectSpouseName) {
      if (!asString(formData.spouseName)) {
        errors.spouseName = 'SPOUSE NAME IS REQUIRED.';
      }
    }

    if (!employeePhotoDocumentType?.id) {
      errors.photoDocument = 'Employee Photo document type is not configured. Please contact Admin.';
      setPhotoUploadError(errors.photoDocument);
    } else if (!photoDocument) {
      errors.photoDocument = 'EMPLOYEE PHOTO IS REQUIRED.';
      setPhotoUploadError('EMPLOYEE PHOTO IS REQUIRED.');
    } else {
      setPhotoUploadError('');
    }

    if (educationMandatory) {
      if (qualificationEntries.length === 0) {
        errors.qualificationsField = 'ADD AT LEAST ONE QUALIFICATION.';
        setQualificationError('ADD AT LEAST ONE QUALIFICATION.');
      } else {
        setQualificationError('');
      }
    } else {
      setQualificationError('');
    }

    const providedDocuments = photoDocument ? [...documents, photoDocument] : [...documents];
    const providedTypeIds = new Set(
      providedDocuments
        .map((doc) => doc?.documentTypeId)
        .filter((id) => id !== undefined && id !== null && `${id}`.length > 0)
        .map((id) => `${id}`),
    );

    const missingMandatoryDocuments = mandatoryDocumentTypes.filter(
      (type) => !providedTypeIds.has(`${type.id}`),
    );

    if (documents.length === 0) {
      errors.documentsField = 'ADD AT LEAST ONE DOCUMENT.';
      setDocumentError('ADD AT LEAST ONE DOCUMENT.');
    } else if (missingMandatoryDocuments.length > 0) {
      const missingNames = missingMandatoryDocuments.map((type) => type.name.toUpperCase()).join(', ');
      errors.documentsField = `MANDATORY DOCUMENTS MISSING: ${missingNames}.`;
      setDocumentError(`MANDATORY DOCUMENTS MISSING: ${missingNames}.`);
    } else {
      setDocumentError('');
    }

    const totalFamilyMembers = familyMembers.length + (shouldCollectSpouseName ? 1 : 0);
    if (totalFamilyMembers === 0) {
      errors.familyField = 'ADD AT LEAST ONE FAMILY MEMBER.';
      setFamilyError('ADD AT LEAST ONE FAMILY MEMBER.');
    } else {
      setFamilyError('');
    }

    return errors;
  };

  const buildFamilyMembersPayload = () => {
    const members = familyMembers.map((member) => ({
      relation: member.relation,
      name: member.name,
      mobile: member.mobile,
      dob: member.dob,
    }));

    if (shouldCollectSpouseName) {
      const spouseEntry = {
        relation: 'SPOUSE',
        name: formData.spouseName.toUpperCase(),
        mobile: '',
        dob: '',
      };
      const existingIndex = members.findIndex(
        (member) => (member.relation || '').toUpperCase() === 'SPOUSE',
      );
      if (existingIndex >= 0) {
        members[existingIndex] = { ...members[existingIndex], name: spouseEntry.name };
      } else {
        members.push(spouseEntry);
      }
    }

    return members;
  };

  const buildQualificationPayload = () =>
    qualificationEntries.map((entry) => ({
      qualificationType: entry.qualificationType,
      courseName: entry.courseName,
      institutionName: entry.institutionName,
      yearOfPassing: entry.yearOfPassing,
      gradeOrPercentage: entry.gradeOrPercentage,
      documentPath: entry.documentPath,
      fileName: entry.fileName,
    }));

  const buildDocumentPayload = () => {
    const docs = documents.map((doc) => ({
      documentTypeId: doc.documentTypeId ?? null,
      documentType: doc.documentType,
      documentTypeName: doc.documentTypeName || doc.documentType,
      fileName: doc.fileName,
      filePath: doc.filePath,
    }));

    if (photoDocument) {
      docs.unshift({
        documentTypeId: photoDocument.documentTypeId ?? null,
        documentType: photoDocument.documentType,
        documentTypeName: photoDocument.documentTypeName || photoDocument.documentType,
        fileName: photoDocument.fileName,
        filePath: photoDocument.filePath,
      });
    }

    return docs;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSubmitStatus({ type: 'error', message: 'PLEASE REVIEW THE HIGHLIGHTED FIELDS.' });
      scrollToFirstError(errors);
      return;
    }

    const professionalPayload = hasExperience
      ? {
          lastCompany: formData.lastCompany,
          designation: formData.designation,
          fromDate: formData.fromDate,
          toDate: formData.toDate,
        }
      : null;

    const payload = {
      personal: {
        name: formData.fullName,
        dob: formData.dob,
        gender: formData.gender,
        maritalStatus: formData.maritalStatus,
        fatherName: formData.fatherName,
        phone: formData.phone,
        aadhar: formData.aadhaar,
        email: formData.email,
        currentAddress: formData.currentAddress,
        permanentAddress: sameAsCurrent ? formData.currentAddress : formData.permanentAddress,
        sameAsCurrentAddress: sameAsCurrent,
        spouseName: shouldCollectSpouseName ? formData.spouseName : '',
      },
      pf: {
        uanNumber: formData.uan,
        esiNumber: formData.esi,
      },
      bank: {
        accountNumber: formData.accountNumber,
        ifscCode: formData.ifsc,
        bankName: formData.bankName,
        panCard: formData.panNumber,
        drivingLicense: formData.drivingLicense,
      },
      education: {},
      professional: professionalPayload,
      family: {
        members: buildFamilyMembersPayload(),
      },
      documents: buildDocumentPayload(),
      qualifications: buildQualificationPayload(),
    };

    try {
      setIsSubmitting(true);
      setSubmitStatus(null);
      const response = await employeeApiRequest('/api/public/employee/register', {
        method: 'POST',
        body: payload,
      });

      const photoUrl = buildAbsoluteUrl(photoDocument?.fileUrl || photoDocument?.filePath);

      const submissionTimestamp = new Date().toISOString();
      const updatedProfile = {
        ...(employeeProfile ?? {}),
        registrationStatus: 'submitted',
        mobileRegistrationStatus: 'submitted',
        mobileRegistrationSubmitted: true,
        mobileRegistrationSubmittedAt: submissionTimestamp,
        meta: {
          ...(employeeProfile?.meta ?? {}),
          mobileRegistrationStatus: 'submitted',
          mobileRegistrationSubmitted: true,
          mobileRegistrationSubmittedAt: submissionTimestamp,
        },
      };
      setEmployeeProfile(updatedProfile);
      updateStoredEmployeeProfile(updatedProfile);

      navigate('/employee/register/success', {
        replace: true,
        state: {
          id: response?.data?.id ?? null,
          name: formData.fullName,
          photoUrl: photoUrl ? `${photoUrl}${photoUrl.includes('?') ? '&' : '?'}v=${Date.now()}` : '',
          updatedAt: submissionTimestamp,
        },
      });
      clearEmployeeRegistrationDraft();

      setFormData(initialFormState);
      setSameAsCurrent(false);
      setFormErrors({});
      setFamilyMembers([]);
      setFamilyMemberForm({ relation: '', name: '', mobile: '', dob: '' });
      setDocuments([]);
      setDocumentDraft({ documentType: '', file: null, fileName: '' });
      setQualificationEntries([]);
      setQualificationDraft(emptyQualificationDraft);
      setPhotoDocument(null);
      updatePhotoPreview('');
      setHasExperience(false);
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'FAILED TO SUBMIT REGISTRATION. PLEASE TRY AGAIN.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const draft = getEmployeeRegistrationDraft();
    if (!draft || isRegistrationBlocked) {
      return;
    }
    if (draft.formData) {
      setFormData((prev) => ({ ...prev, ...draft.formData }));
    }
    if (Array.isArray(draft.familyMembers)) setFamilyMembers(draft.familyMembers);
    if (Array.isArray(draft.documents)) setDocuments(draft.documents);
    if (Array.isArray(draft.qualificationEntries)) setQualificationEntries(draft.qualificationEntries);
    if (draft.photoDocument) setPhotoDocument(draft.photoDocument);
    if (typeof draft.hasExperience === 'boolean') setHasExperience(draft.hasExperience);
    if (typeof draft.sameAsCurrent === 'boolean') setSameAsCurrent(draft.sameAsCurrent);
  }, [isRegistrationBlocked]);

  useEffect(() => {
    if (isRegistrationBlocked) return;
    setEmployeeRegistrationDraft({
      formData,
      familyMembers,
      documents,
      qualificationEntries,
      photoDocument,
      hasExperience,
      sameAsCurrent,
    });
  }, [documents, familyMembers, formData, hasExperience, isRegistrationBlocked, photoDocument, qualificationEntries, sameAsCurrent]);

  const renderAppBar = () => (
    <div className="employee-register-appbar">
      <button type="button" className="employee-register-home-button" onClick={handleNavigateHome}>
        <FiHome aria-hidden="true" />
        <span>HOME</span>
      </button>
      <div className="employee-register-profile">
        <div
          className={`employee-register-avatar${shouldShowHeaderPhoto ? ' employee-register-avatar--image' : ''}`}
          aria-hidden={shouldShowHeaderPhoto ? undefined : true}
        >
          {shouldShowHeaderPhoto ? (
            <img
              src={employeePhotoUrl}
              alt={employeeAvatarAlt}
              onError={() => setHeaderPhotoLoadFailed(true)}
            />
          ) : (
            getInitials(employeeName)
          )}
        </div>
        <div className="employee-register-profile-text">
          <span className="employee-register-profile-name">{employeeName}</span>
          {companyName && <span className="employee-register-profile-company">{companyName}</span>}
        </div>
      </div>
    </div>
  );

  const blockedMessage = registrationStatus === 'submitted'
    ? 'You have already submitted your registration details from this device.'
    : 'Your profile has already been approved. Please contact HR for any updates.';

  if (isRegistrationBlocked) {
    return (
      <div className="employee-register-page">
        {renderAppBar()}
        <div className="employee-register-blocked">
          <div className="employee-register-blocked-card">
            <h1>REGISTRATION NOT AVAILABLE</h1>
            <p>{blockedMessage}</p>
            <button type="button" className="employee-register-submit" onClick={handleNavigateHome}>
              BACK TO DASHBOARD
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-register-page">
      {renderAppBar()}
      <header className="employee-register-header">
        <div className="employee-register-icon">
          <PersonFill />
        </div>
        <div>
          <h1>EMPLOYEE REGISTRATION</h1>
          <p>COMPLETE THE FORM BELOW TO JOIN THE ORGANISATION</p>
        </div>
      </header>

      {submitStatus?.type === 'error' && (
        <div className="employee-register-alert employee-register-alert-error" role="alert">
          {submitStatus.message}
        </div>
      )}

      <form className="employee-register-form" onSubmit={handleSubmit} autoComplete="off">
        <section className="employee-register-section">
          <div className="employee-register-section-heading">
            <span className="employee-register-section-icon">{sectionHeadings[0].icon}</span>
            <h2>{sectionHeadings[0].title}</h2>
          </div>
          <div className="employee-register-grid">
            <label className="employee-register-field">
              <span>NAME</span>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="ENTER FULL NAME"
                className={formErrors.fullName ? 'employee-register-input-error' : ''}
                autoComplete="off"
              />
              {formErrors.fullName && <p className="employee-register-error">{formErrors.fullName}</p>}
            </label>
            <label className="employee-register-field">
              <span>DATE OF BIRTH</span>
              <input
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                className={formErrors.dob ? 'employee-register-input-error' : ''}
                autoComplete="off"
              />
              {formErrors.dob && <p className="employee-register-error">{formErrors.dob}</p>}
            </label>
            <label className="employee-register-field">
              <span>GENDER</span>
              <select
                name="gender"
                value={formData.gender}
              onChange={handleChange}
              className={formErrors.gender ? 'employee-register-input-error' : ''}
              autoComplete="off"
            >
                <option value="">Select Option</option>
                {genderOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {formErrors.gender && <p className="employee-register-error">{formErrors.gender}</p>}
            </label>
            <label className="employee-register-field">
              <span>MARITAL STATUS</span>
              <select
                name="maritalStatus"
                value={formData.maritalStatus}
              onChange={handleChange}
              className={formErrors.maritalStatus ? 'employee-register-input-error' : ''}
              autoComplete="off"
            >
                <option value="">Select Option</option>
                {maritalOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {formErrors.maritalStatus && <p className="employee-register-error">{formErrors.maritalStatus}</p>}
            </label>
            {shouldCollectSpouseName && (
              <label className="employee-register-field">
                <span>SPOUSE NAME</span>
                <input
                  type="text"
                  name="spouseName"
                  value={formData.spouseName}
                  onChange={handleChange}
                  placeholder="ENTER SPOUSE NAME"
                  className={formErrors.spouseName ? 'employee-register-input-error' : ''}
                  autoComplete="off"
                />
                {formErrors.spouseName && <p className="employee-register-error">{formErrors.spouseName}</p>}
              </label>
            )}
            <label className="employee-register-field">
              <span>FATHER NAME</span>
              <input
                type="text"
                name="fatherName"
                value={formData.fatherName}
                onChange={handleChange}
                placeholder="ENTER FATHER NAME"
                className={formErrors.fatherName ? 'employee-register-input-error' : ''}
                autoComplete="off"
              />
              {formErrors.fatherName && <p className="employee-register-error">{formErrors.fatherName}</p>}
            </label>
            <label className="employee-register-field">
              <span>PHONE NUMBER</span>
              <div className={`employee-register-phone ${formErrors.phone ? 'employee-register-phone-error' : ''}`}>
                <span className="employee-register-phone-code">+91</span>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="9876543210"
                  pattern="[0-9]{10}"
                  maxLength={10}
                  inputMode="numeric"
                  autoComplete="off"
                />
              </div>
              {formErrors.phone && <p className="employee-register-error">{formErrors.phone}</p>}
            </label>
            <label className="employee-register-field">
              <span>AADHAAR NUMBER</span>
              <input
                type="text"
                name="aadhaar"
                value={formData.aadhaar}
                onChange={handleChange}
                placeholder="XXXXXXXXXXXX"
                maxLength={12}
                inputMode="numeric"
                className={formErrors.aadhaar ? 'employee-register-input-error' : ''}
                autoComplete="off"
              />
              {formErrors.aadhaar && <p className="employee-register-error">{formErrors.aadhaar}</p>}
            </label>
            <label className="employee-register-field">
              <span>MAIL ID</span>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="EXAMPLE@EMAIL.COM"
                className={formErrors.email ? 'employee-register-input-error' : ''}
                autoComplete="off"
              />
              {formErrors.email && <p className="employee-register-error">{formErrors.email}</p>}
            </label>
            <label className="employee-register-field">
              <span>CURRENT ADDRESS</span>
              <textarea
                name="currentAddress"
                value={formData.currentAddress}
                onChange={handleChange}
                rows={3}
                className={formErrors.currentAddress ? 'employee-register-input-error' : ''}
                autoComplete="off"
              />
              {formErrors.currentAddress && <p className="employee-register-error">{formErrors.currentAddress}</p>}
            </label>
            <label className="employee-register-field">
              <span>PERMANENT ADDRESS</span>
              <textarea
                name="permanentAddress"
                value={formData.permanentAddress}
                onChange={handleChange}
                rows={3}
                className={formErrors.permanentAddress ? 'employee-register-input-error' : ''}
                autoComplete="off"
                disabled={sameAsCurrent}
              />
              {formErrors.permanentAddress && <p className="employee-register-error">{formErrors.permanentAddress}</p>}
            </label>
            <label className="employee-register-checkbox">
              <input
                type="checkbox"
                checked={sameAsCurrent}
                onChange={handleSameAddressToggle}
              />
              <span>SELECT THE BOX IF CURRENT ADDRESS AS PERMANENT ADDRESS</span>
            </label>
          </div>
        </section>

        <section className="employee-register-section">
          <div className="employee-register-section-heading">
            <span className="employee-register-section-icon">{sectionHeadings[1].icon}</span>
            <h2>{sectionHeadings[1].title}</h2>
          </div>
          <div className="employee-register-grid">
            <label className="employee-register-field">
              <span>UAN NUMBER</span>
              <input
                type="text"
                name="uan"
                value={formData.uan}
                onChange={handleChange}
                placeholder="ENTER UAN NUMBER"
                className={formErrors.uan ? 'employee-register-input-error' : ''}
                autoComplete="off"
              />
              {formErrors.uan && <p className="employee-register-error">{formErrors.uan}</p>}
            </label>
            <label className="employee-register-field">
              <span>ESI NUMBER</span>
              <input
                type="text"
                name="esi"
                value={formData.esi}
                onChange={handleChange}
                placeholder="ENTER ESI NUMBER"
                className={formErrors.esi ? 'employee-register-input-error' : ''}
                autoComplete="off"
              />
              {formErrors.esi && <p className="employee-register-error">{formErrors.esi}</p>}
            </label>
          </div>
        </section>

        <section className="employee-register-section">
          <div className="employee-register-section-heading">
            <span className="employee-register-section-icon">{sectionHeadings[2].icon}</span>
            <h2>{sectionHeadings[2].title}</h2>
          </div>
          <div className="employee-register-grid">
            <label className="employee-register-field">
              <span>ACCOUNT NUMBER</span>
              <input
                type="password"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                placeholder="ENTER ACCOUNT NUMBER"
                className={formErrors.accountNumber ? 'employee-register-input-error' : ''}
                autoComplete="off"
                inputMode="numeric"
              />
              {formErrors.accountNumber && <p className="employee-register-error">{formErrors.accountNumber}</p>}
            </label>
            <label className="employee-register-field">
              <span>RE-ENTER ACCOUNT NUMBER</span>
              <input
                type="text"
                name="confirmAccountNumber"
                value={formData.confirmAccountNumber}
                onChange={handleChange}
                placeholder="RE-ENTER ACCOUNT NUMBER"
                className={formErrors.confirmAccountNumber ? 'employee-register-input-error' : ''}
                autoComplete="off"
                inputMode="numeric"
              />
              {formErrors.confirmAccountNumber && <p className="employee-register-error">{formErrors.confirmAccountNumber}</p>}
            </label>
            <label className="employee-register-field">
              <span>IFSC CODE</span>
              <input
                type="text"
                name="ifsc"
                value={formData.ifsc}
                onChange={handleChange}
                placeholder="ENTER IFSC CODE"
                className={formErrors.ifsc ? 'employee-register-input-error' : ''}
                autoComplete="off"
              />
              {formErrors.ifsc && <p className="employee-register-error">{formErrors.ifsc}</p>}
            </label>
            <label className="employee-register-field">
              <span>BANK NAME</span>
              <select
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                className={formErrors.bankName ? 'employee-register-input-error' : ''}
                autoComplete="off"
              >
                <option value="">{isLoadingBanks ? 'LOADING BANKS...' : 'SELECT BANK'}</option>
                {banks.map((bank) => (
                  <option key={bank.id} value={bank.bankName ? bank.bankName.toUpperCase() : ''}>
                    {(bank.bankName || '').toUpperCase()}
                  </option>
                ))}
              </select>
              {bankError && !formErrors.bankName && (
                <p className="employee-register-error">{bankError.toUpperCase()}</p>
              )}
              {formErrors.bankName && <p className="employee-register-error">{formErrors.bankName}</p>}
            </label>
            <label className="employee-register-field">
              <span>PAN CARD NUMBER</span>
              <input
                type="text"
                name="panNumber"
                value={formData.panNumber}
                onChange={handleChange}
                placeholder="ABCDE1234F"
                maxLength={10}
                className={formErrors.panNumber ? 'employee-register-input-error' : ''}
                autoComplete="off"
              />
              {formErrors.panNumber && <p className="employee-register-error">{formErrors.panNumber}</p>}
            </label>
            <label className="employee-register-field">
              <span>DRIVING LICENSE NUMBER</span>
              <input
                type="text"
                name="drivingLicense"
                value={formData.drivingLicense}
                onChange={handleChange}
                placeholder="ENTER DL NUMBER"
                className={formErrors.drivingLicense ? 'employee-register-input-error' : ''}
                autoComplete="off"
              />
              {formErrors.drivingLicense && <p className="employee-register-error">{formErrors.drivingLicense}</p>}
            </label>
          </div>
        </section>

        <section className="employee-register-section">
          <div className="employee-register-section-heading">
            <span className="employee-register-section-icon">{sectionHeadings[3].icon}</span>
            <h2>{sectionHeadings[3].title}</h2>
          </div>
          <p className="employee-register-hint">
            {educationMandatory
              ? 'Educational qualifications are required for your login. Add at least one entry.'
              : 'Education details are optional for this login. You may skip this section if not applicable.'}
          </p>
          <div className="employee-register-grid">
            <label className="employee-register-field">
              <span>EDUCATION QUALIFICATION</span>
              <select
                name="qualificationType"
                value={qualificationDraft.qualificationType}
                onChange={handleQualificationDraftChange}
                autoComplete="off"
              >
                <option value="">SELECT QUALIFICATION</option>
                {qualificationOptions.map((option) => (
                  <option key={option.id} value={option.code}>{(option.title || option.code || '').toUpperCase()}</option>
                ))}
              </select>
              {qualificationOptionsError && (
                <p className="employee-register-error">{qualificationOptionsError.toUpperCase()}</p>
              )}
            </label>
            <label className="employee-register-field">
              <span>COURSE NAME</span>
              <input
                type="text"
                name="courseName"
                value={qualificationDraft.courseName}
                onChange={handleQualificationDraftChange}
                placeholder="ENTER COURSE"
                autoComplete="off"
              />
            </label>
            <label className="employee-register-field">
              <span>INSTITUTION NAME</span>
              <input
                type="text"
                name="institutionName"
                value={qualificationDraft.institutionName}
                onChange={handleQualificationDraftChange}
                placeholder="ENTER INSTITUTION"
                autoComplete="off"
              />
            </label>
            <div className="employee-register-inline">
              <label className="employee-register-field">
                <span>YEAR OF PASSING</span>
                <input
                  type="text"
                  name="yearOfPassing"
                  value={qualificationDraft.yearOfPassing}
                  onChange={handleQualificationDraftChange}
                  placeholder="YYYY"
                  maxLength={4}
                  inputMode="numeric"
                  autoComplete="off"
                />
              </label>
              <label className="employee-register-field">
                <span>GRADE / PERCENTAGE</span>
                <input
                  type="text"
                  name="gradeOrPercentage"
                  value={qualificationDraft.gradeOrPercentage}
                  onChange={handleQualificationDraftChange}
                  placeholder="ENTER GRADE"
                  autoComplete="off"
                />
              </label>
            </div>
            <label className="employee-register-field">
              <span>UPLOAD QUALIFICATION DOCUMENT</span>
              <input
                type="file"
                name="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleQualificationDraftChange}
                ref={qualificationFileInputRef}
                autoComplete="off"
              />
            </label>
            {qualificationDraft.fileName && (
              <p className="employee-register-file-name">SELECTED: {formatFileName(qualificationDraft.fileName)}</p>
            )}
            {qualificationError && <p className="employee-register-error">{qualificationError}</p>}
            <button
              type="button"
              className="employee-register-add"
              onClick={handleAddQualification}
              disabled={isQualificationUploading}
            >
              {isQualificationUploading ? 'UPLOADING...' : (<><PlusLg /> ADD QUALIFICATION</>)}
            </button>
            <input type="hidden" name="qualificationsField" value={qualificationEntries.length ? '1' : ''} />
            {qualificationEntries.length > 0 && (
              <div className="employee-register-list">
                {qualificationEntries.map((entry) => (
                  <div key={entry.id} className="employee-register-card">
                    <div className="employee-register-card-content">
                      <p className="employee-register-card-title">{entry.qualificationType}</p>
                      <p className="employee-register-card-detail">
                        {[entry.courseName, entry.institutionName].filter(Boolean).join(' • ')}
                      </p>
                      <p className="employee-register-card-detail">
                        {[entry.yearOfPassing, entry.gradeOrPercentage].filter(Boolean).join(' • ')}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="employee-register-card-remove"
                      onClick={() => handleRemoveQualification(entry.id)}
                      aria-label="REMOVE QUALIFICATION"
                    >
                      <Trash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="employee-register-section">
          <div className="employee-register-section-heading">
            <span className="employee-register-section-icon">{sectionHeadings[4].icon}</span>
            <h2>{sectionHeadings[4].title}</h2>
          </div>
          <div className="employee-register-grid employee-register-grid--single">
            <div className="employee-register-field employee-register-toggle">
              <span>DO YOU HAVE EXPERIENCE?</span>
              <div className="employee-register-toggle-group" role="radiogroup" aria-label="Experience selection">
                <label className="employee-register-toggle-option">
                  <input
                    type="radio"
                    name="hasExperience"
                    value="yes"
                    checked={hasExperience}
                    onChange={() => handleExperienceToggle(true)}
                  />
                  <span>YES</span>
                </label>
                <label className="employee-register-toggle-option">
                  <input
                    type="radio"
                    name="hasExperience"
                    value="no"
                    checked={!hasExperience}
                    onChange={() => handleExperienceToggle(false)}
                  />
                  <span>NO</span>
                </label>
              </div>
            </div>
          </div>
          {hasExperience && (
            <div className="employee-register-grid">
              <label className="employee-register-field">
                <span>LAST COMPANY NAME</span>
                <input
                  type="text"
                  name="lastCompany"
                  value={formData.lastCompany}
                  onChange={handleChange}
                  placeholder="ENTER COMPANY NAME"
                  className={formErrors.lastCompany ? 'employee-register-input-error' : ''}
                  autoComplete="off"
                />
                {formErrors.lastCompany && <p className="employee-register-error">{formErrors.lastCompany}</p>}
              </label>
              <label className="employee-register-field">
                <span>PREVIOUS COMPANY DESIGNATION</span>
                <input
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  placeholder="ENTER PREVIOUS COMPANY DESIGNATION"
                  className={formErrors.designation ? 'employee-register-input-error' : ''}
                  autoComplete="off"
                />
                {formErrors.designation && <p className="employee-register-error">{formErrors.designation}</p>}
              </label>
              <div className="employee-register-inline">
                <label className="employee-register-field">
                  <span>FROM DATE</span>
                  <input
                    type="date"
                    name="fromDate"
                    value={formData.fromDate}
                    onChange={handleChange}
                    className={formErrors.fromDate ? 'employee-register-input-error' : ''}
                    autoComplete="off"
                  />
                  {formErrors.fromDate && <p className="employee-register-error">{formErrors.fromDate}</p>}
                </label>
                <label className="employee-register-field">
                  <span>TO DATE</span>
                  <input
                    type="date"
                    name="toDate"
                    value={formData.toDate}
                    onChange={handleChange}
                    className={formErrors.toDate ? 'employee-register-input-error' : ''}
                    autoComplete="off"
                  />
                  {formErrors.toDate && <p className="employee-register-error">{formErrors.toDate}</p>}
                </label>
              </div>
            </div>
          )}
        </section>

        <section className="employee-register-section">
          <div className="employee-register-section-heading">
            <span className="employee-register-section-icon">{sectionHeadings[5].icon}</span>
            <h2>{sectionHeadings[5].title}</h2>
          </div>
          <div className="employee-register-grid">
            <label className="employee-register-field">
              <span>RELATION</span>
              <select
                name="relation"
                value={familyMemberForm.relation}
                onChange={handleFamilyMemberChange}
                autoComplete="off"
              >
                <option value="">SELECT RELATION</option>
                {relationOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
            <label className="employee-register-field">
              <span>NAME</span>
              <input
                type="text"
                name="name"
                value={familyMemberForm.name}
                onChange={handleFamilyMemberChange}
                placeholder="ENTER NAME"
                autoComplete="off"
              />
            </label>
            <label className="employee-register-field">
              <span>MOBILE NUMBER</span>
              <input
                type="tel"
                name="mobile"
                value={familyMemberForm.mobile}
                onChange={handleFamilyMemberChange}
                placeholder="9876543210"
                pattern="[0-9]{10}"
                maxLength={10}
                inputMode="numeric"
                autoComplete="off"
              />
            </label>
            <label className="employee-register-field">
              <span>DATE OF BIRTH</span>
              <input
                type="date"
                name="dob"
                value={familyMemberForm.dob}
                onChange={handleFamilyMemberChange}
                autoComplete="off"
              />
            </label>
            {familyError && <p className="employee-register-error">{familyError}</p>}
            <button type="button" className="employee-register-add" onClick={handleAddFamilyMember}>
              <PlusLg /> ADD FAMILY MEMBER
            </button>
            <input type="hidden" name="familyField" value={familyMembers.length ? '1' : ''} />
            {familyMembers.length > 0 && (
              <div className="employee-register-list">
                {familyMembers.map((member) => (
                  <div key={member.id} className="employee-register-card">
                    <div>
                      <p className="employee-register-card-title">{member.relation}</p>
                      <p className="employee-register-card-detail">{member.name}</p>
                      {(member.mobile || member.dob) && (
                        <p className="employee-register-card-detail">
                          {[member.mobile, member.dob].filter(Boolean).join(' • ')}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      className="employee-register-card-remove"
                      onClick={() => handleRemoveFamilyMember(member.id)}
                      aria-label="REMOVE FAMILY MEMBER"
                    >
                      <Trash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="employee-register-section">
          <div className="employee-register-section-heading">
            <span className="employee-register-section-icon">{sectionHeadings[6].icon}</span>
            <h2>{sectionHeadings[6].title}</h2>
          </div>
          <div className="employee-register-grid">
            <div className="employee-register-field">
              <span>UPLOAD EMPLOYEE PHOTO</span>
              <input
                type="file"
                name="employeePhoto"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={handlePhotoFileChange}
                ref={photoFileInputRef}
                autoComplete="off"
              />
            </div>
            <input type="hidden" name="photoDocument" value={photoDocument?.filePath || ''} />
            {photoUploadError && <p className="employee-register-error">{photoUploadError}</p>}
            {isCroppingPhoto && (
              <div className="employee-register-photo-container">
                <div className="employee-register-photo-cropper">
                  <Cropper
                    image={photoSource}
                    crop={photoCrop}
                    zoom={photoZoom}
                    aspect={3.5 / 4.5}
                    onCropChange={setPhotoCrop}
                    onZoomChange={setPhotoZoom}
                    onCropComplete={handlePhotoCropComplete}
                  />
                </div>
                <div className="employee-register-photo-controls">
                  <label className="employee-register-field">
                    <span>ZOOM</span>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.1"
                      value={photoZoom}
                      onChange={(event) => setPhotoZoom(Number.parseFloat(event.target.value) || 1)}
                      className="employee-register-slider"
                    />
                  </label>
                  <div className="employee-register-inline">
                    <button type="button" className="employee-register-add" onClick={handleCancelPhotoCrop}>
                      CANCEL
                    </button>
                    <button
                      type="button"
                      className="employee-register-add"
                      onClick={handleConfirmPhoto}
                      disabled={isPhotoUploading}
                    >
                      {isPhotoUploading ? 'UPLOADING...' : 'SAVE PHOTO'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            {photoPreview && !isCroppingPhoto && (
              <div className="employee-register-photo-preview">
                <img src={photoPreview} alt="EMPLOYEE" />
              </div>
            )}
          </div>
        </section>

        <section className="employee-register-section">
          <div className="employee-register-section-heading">
            <span className="employee-register-section-icon">{sectionHeadings[7].icon}</span>
            <h2>{sectionHeadings[7].title}</h2>
          </div>
          <div className="employee-register-grid">
            <label className="employee-register-field">
              <span>SELECT DOCUMENT</span>
              <select
                name="documentTypeId"
                value={documentDraft.documentTypeId}
                onChange={handleDocumentDraftChange}
                autoComplete="off"
              >
                <option value="">
                  {isLoadingDocumentTypes ? 'Loading document types...' : 'Select Option'}
                </option>
                {availableDocumentTypes.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                    {option.isMandatory ? ' (Mandatory)' : ''}
                  </option>
                ))}
              </select>
            </label>
            {documentTypeError && <p className="employee-register-error">{documentTypeError}</p>}
            <label className="employee-register-field">
              <span>UPLOAD FILE</span>
              <input
                type="file"
                name="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleDocumentDraftChange}
                ref={documentFileInputRef}
                autoComplete="off"
              />
            </label>
            {documentDraft.fileName && (
              <p className="employee-register-file-name">SELECTED: {formatFileName(documentDraft.fileName)}</p>
            )}
            {documentError && <p className="employee-register-error">{documentError}</p>}
            <button
              type="button"
              className="employee-register-add"
              onClick={handleAddDocument}
              disabled={isDocumentUploading}
            >
              {isDocumentUploading ? 'UPLOADING...' : (<><PlusLg /> ADD DOCUMENT</>)}
            </button>
            <input
              type="hidden"
              name="documentsField"
              value={(documents.length + (photoDocument ? 1 : 0)) ? '1' : ''}
            />
            {documents.length > 0 && (
              <div className="employee-register-list">
                {documents.map((doc) => (
                  <div key={doc.id} className="employee-register-card">
                    <button
                      type="button"
                      className="employee-register-card-remove"
                      onClick={() => handleRemoveDocument(doc.id)}
                      aria-label="REMOVE DOCUMENT"
                    >
                      <Trash />
                    </button>
                    <div className="employee-register-card-content">
                      <p className="employee-register-card-title">{doc.documentType}</p>
                      <p className="employee-register-card-detail" title={doc.fileName}>
                        {formatFileName(doc.fileName)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <div className="employee-register-actions">
          <button
            type="submit"
            className="employee-register-submit"
            disabled={
              isSubmitting ||
              isPhotoUploading ||
              isDocumentUploading ||
              isQualificationUploading
            }
          >
            {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EmployeeRegister;
